import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  campaignLogicInitialState,
  SOCIAL_CAMPAIGN_IDENTITIES_INITIAL_STATE,
} from './campaignLogic.config';
import {SelectedNode} from './campaignLogic.types';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {DisconnectedNodes} from 'common/src/modules/nodes/nodes.types';
import {CampaignTreeType} from '../../campaigns.types';
import {MapCardConnector} from 'common/src/components/CLCardsMap/Canvas/Canvas.types';
import {SocialNetworkPage} from 'common/src/modules/social/social.types';

const campaignLogicSlice = createSlice({
  name: BreadcrumbName.CampaignLogic,
  initialState: campaignLogicInitialState,
  reducers: {
    setNodes: (
      state,
      action: PayloadAction<{nodes: DisconnectedNodes; type: CampaignTreeType}>,
    ) => {
      state.nodes[action.payload.type] = action.payload.nodes;
    },
    selectNode: (state, action: PayloadAction<SelectedNode>) => {
      state.selectedNode = action.payload;
    },
    resetSelectedNode: (state) => {
      state.selectedNode = null;
    },
    setNextNodeId: (state, action: PayloadAction<number>) => {
      state.nextNodeId = action.payload;
    },
    setNodesByZIndexOrder: (
      state,
      action: PayloadAction<{order: number[]; type: CampaignTreeType}>,
    ) => {
      state.nodesByZIndexOrder[action.payload.type] = action.payload.order;
    },
    setNodesWithDisabledHideOutputButton: (
      state,
      action: PayloadAction<Record<number, number[]>>,
    ) => {
      state.nodesWithDisabledHideOutputButton = action.payload;
    },
    setHiddenNodeConnectors: (state, action: PayloadAction<MapCardConnector[]>) => {
      state.hiddenNodeConnectors = action.payload;
    },
    setShouldAutoArrange: (state, action: PayloadAction<boolean>) => {
      state.shouldAutoArrange = action.payload;
    },
    setFacebookPageOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.facebook.facebookPageOptions = action.payload;
    },
    setFacebookPageOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.facebook.facebookPageOptionsLoading = action.payload;
    },
    setInstagramAccountOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.facebook.instagramAccountOptions = action.payload;
    },
    setInstagramAccountOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.facebook.instagramAccountOptionsLoading = action.payload;
    },
    setFacebookPixelOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.facebook.facebookPixelOptions = action.payload;
    },
    setFacebookPixelOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.facebook.facebookPixelOptionsLoading = action.payload;
    },
    setYoutubeChannelOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.youtube.youtubeChannelOptions = action.payload;
    },
    setYoutubeChannelOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.youtube.youtubeChannelOptionsLoading = action.payload;
    },
    setSnapchatPublicProfileOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.snapchat.snapchatPublicProfileOptions = action.payload;
    },
    setSnapchatPublicProfileOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.snapchat.snapchatPublicProfileOptionsLoading = action.payload;
    },
    setTiktokIdentityOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.tiktok.tiktokIdentityOptions = action.payload;
    },
    setTiktokIdentityOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.tiktok.tiktokIdentityOptionsLoading = action.payload;
    },
    setTiktokPixelOptions: (state, action: PayloadAction<SocialNetworkPage[]>) => {
      state.socialCampaignIdentities.tiktok.tiktokPixelOptions = action.payload;
    },
    setTiktokPixelOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.socialCampaignIdentities.tiktok.tiktokPixelOptionsLoading = action.payload;
    },
    resetSocialCampaignIdentities: (state) => {
      state.socialCampaignIdentities = SOCIAL_CAMPAIGN_IDENTITIES_INITIAL_STATE;
    },
  },
});

export const {
  selectNode,
  resetSelectedNode,
  setNodes,
  setNextNodeId,
  setNodesByZIndexOrder,
  setNodesWithDisabledHideOutputButton,
  setHiddenNodeConnectors,
  setShouldAutoArrange,
  setFacebookPageOptions,
  setFacebookPageOptionsLoading,
  setInstagramAccountOptions,
  setInstagramAccountOptionsLoading,
  setFacebookPixelOptions,
  setFacebookPixelOptionsLoading,
  setYoutubeChannelOptions,
  setYoutubeChannelOptionsLoading,
  setSnapchatPublicProfileOptions,
  setSnapchatPublicProfileOptionsLoading,
  setTiktokIdentityOptions,
  setTiktokIdentityOptionsLoading,
  setTiktokPixelOptions,
  setTiktokPixelOptionsLoading,
  resetSocialCampaignIdentities,
} = campaignLogicSlice.actions;

export default campaignLogicSlice.reducer;
